import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "canvas", "toolButton"]

  static values = {
    projectId: Number,
    baseUrl: String
  }

  connect() {
    this._fabricCanvas = null
    this._photoId = null
    this._currentTool = "pen"
    this._currentColor = "#EF4444"
    this._currentWidth = 4
    this._history = []
    this._historyIndex = -1
    this._isDrawingShape = false
  }

  // ── Public API ────────────────────────────────────────────────────

  async open({ photoId, imageUrl, existingAnnotations, onClose, onSave, saveUrl, currentUserName }) {
    this._photoId = photoId
    this._saveUrl = saveUrl || null
    this._onCloseCallback = onClose || null
    this._onSaveCallback = onSave || null
    this._currentUserName = currentUserName || ""
    this.overlayTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    // Lazy-load Fabric.js
    const { Canvas, FabricImage, PencilBrush, Circle, Rect, Line, Textbox, Triangle, Group } = await import("fabric")
    this._fabric = { Canvas, FabricImage, PencilBrush, Circle, Rect, Line, Textbox, Triangle, Group }

    // Load image to get dimensions
    const img = new Image()
    img.crossOrigin = "anonymous"
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = imageUrl
    })

    // Calculate canvas size to fit viewport
    const maxW = window.innerWidth - 40
    const maxH = window.innerHeight - 92 // toolbar height + padding
    const scale = Math.min(maxW / img.width, maxH / img.height, 1)
    const canvasW = Math.round(img.width * scale)
    const canvasH = Math.round(img.height * scale)

    // Initialize Fabric canvas
    this.canvasTarget.width = canvasW
    this.canvasTarget.height = canvasH

    this._fabricCanvas = new Canvas(this.canvasTarget, {
      width: canvasW,
      height: canvasH,
      isDrawingMode: true
    })

    // Set background image – Fabric v7 defaults originX/Y to 'center',
    // so we must set 'left' origin to position correctly at (0,0)
    const fabricImg = await FabricImage.fromURL(imageUrl, { crossOrigin: "anonymous" })
    fabricImg.set({ scaleX: scale, scaleY: scale, left: 0, top: 0, originX: "left", originY: "top" })
    this._backgroundImage = fabricImg
    this._fabricCanvas.backgroundImage = fabricImg
    this._fabricCanvas.renderAll()

    // Set brush
    this._fabricCanvas.freeDrawingBrush = new PencilBrush(this._fabricCanvas)
    this._fabricCanvas.freeDrawingBrush.color = this._currentColor
    this._fabricCanvas.freeDrawingBrush.width = this._currentWidth

    // Load existing annotations
    if (existingAnnotations && existingAnnotations.objects) {
      try {
        const rawObjects = existingAnnotations.objects
        await this._fabricCanvas.loadFromJSON(existingAnnotations)
        // Fabric.js loadFromJSON doesn't restore custom properties —
        // manually copy annotation metadata from raw JSON to Fabric objects
        const loaded = this._fabricCanvas.getObjects()
        for (let i = 0; i < loaded.length && i < rawObjects.length; i++) {
          if (rawObjects[i]._annotatedBy) {
            loaded[i]._annotatedBy = rawObjects[i]._annotatedBy
            loaded[i]._annotatedAt = rawObjects[i]._annotatedAt
          }
        }
        // Re-set background after loading (loadFromJSON clears it)
        this._fabricCanvas.backgroundImage = this._backgroundImage
        this._fabricCanvas.renderAll()
      } catch (e) {
        console.warn("Failed to load existing annotations:", e)
      }
    }

    // History — build initial snapshot with annotation metadata injected
    const initJson = this._fabricCanvas.toJSON(["selectable", "evented"])
    const initFabricObjs = this._fabricCanvas.getObjects()
    const initObjs = initJson.objects || []
    for (let i = 0; i < initObjs.length && i < initFabricObjs.length; i++) {
      if (initFabricObjs[i]._annotatedBy) {
        initObjs[i]._annotatedBy = initFabricObjs[i]._annotatedBy
        initObjs[i]._annotatedAt = initFabricObjs[i]._annotatedAt
      }
    }
    this._history = [initJson]
    this._historyIndex = 0

    // Track changes for history; tag new objects with author metadata
    this._fabricCanvas.on("object:added", (opt) => {
      const obj = opt.target
      if (obj && !obj._annotatedBy && this._currentUserName) {
        obj._annotatedBy = this._currentUserName
        obj._annotatedAt = new Date().toISOString()
      }
      this._saveToHistory()
    })
    this._fabricCanvas.on("object:modified", () => this._saveToHistory())

    // Shape drawing handlers
    this._fabricCanvas.on("mouse:down", (opt) => this._onMouseDown(opt))
    this._fabricCanvas.on("mouse:move", (opt) => this._onMouseMove(opt))
    this._fabricCanvas.on("mouse:up", () => this._onMouseUp())

    this._setTool("pen")

    // Store original dimensions for serialization
    this._originalWidth = canvasW
    this._originalHeight = canvasH

    // Keyboard shortcut: Delete/Backspace removes selected objects
    this._onKeydown = (e) => {
      if ((e.key === "Delete" || e.key === "Backspace") && !e.target.closest("[contenteditable], input, textarea")) {
        const active = this._fabricCanvas?.getActiveObjects()
        if (active?.length) {
          e.preventDefault()
          this.deleteSelected()
        }
      }
    }
    document.addEventListener("keydown", this._onKeydown)
  }

  close() {
    if (this._onKeydown) {
      document.removeEventListener("keydown", this._onKeydown)
      this._onKeydown = null
    }
    if (this._fabricCanvas) {
      this._fabricCanvas.dispose()
      this._fabricCanvas = null
    }
    this.overlayTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._photoId = null
    this._history = []
    this._historyIndex = -1
    if (this._onCloseCallback) {
      this._onCloseCallback()
      this._onCloseCallback = null
    }
  }

  // ── Tools ─────────────────────────────────────────────────────────

  selectTool(e) {
    const tool = e.currentTarget.dataset.tool
    this._setTool(tool)

    // Update UI
    this.toolButtonTargets.forEach(btn => {
      btn.classList.remove("bg-gray-200", "markup-tool-active")
      btn.classList.add("hover:bg-gray-100")
    })
    e.currentTarget.classList.add("bg-gray-200", "markup-tool-active")
    e.currentTarget.classList.remove("hover:bg-gray-100")
  }

  _setTool(tool) {
    this._currentTool = tool
    if (!this._fabricCanvas) return

    if (tool === "pen") {
      this._fabricCanvas.isDrawingMode = true
      this._fabricCanvas.selection = false
      this._fabricCanvas.freeDrawingBrush.color = this._currentColor
      this._fabricCanvas.freeDrawingBrush.width = this._currentWidth
      this._fabricCanvas.forEachObject(o => { o.selectable = false; o.evented = false })
    } else if (tool === "select") {
      this._fabricCanvas.isDrawingMode = false
      this._fabricCanvas.selection = true
      this._fabricCanvas.forEachObject(o => { o.selectable = true; o.evented = true })
    } else {
      this._fabricCanvas.isDrawingMode = false
      this._fabricCanvas.selection = false
      this._fabricCanvas.forEachObject(o => { o.selectable = false; o.evented = false })
    }
  }

  setColor(e) {
    this._currentColor = e.currentTarget.dataset.color

    // Update border on color buttons
    e.currentTarget.parentElement.querySelectorAll("button").forEach(btn => {
      btn.classList.remove("border-gray-800")
      btn.classList.add("border-transparent")
    })
    e.currentTarget.classList.remove("border-transparent")
    e.currentTarget.classList.add("border-gray-800")

    if (this._fabricCanvas && this._fabricCanvas.freeDrawingBrush) {
      this._fabricCanvas.freeDrawingBrush.color = this._currentColor
    }
  }

  setWidth(e) {
    this._currentWidth = parseInt(e.currentTarget.dataset.width) || 4

    // Update UI
    e.currentTarget.parentElement.querySelectorAll("button").forEach(btn => {
      btn.classList.remove("bg-gray-200")
      btn.classList.add("hover:bg-gray-100")
    })
    e.currentTarget.classList.add("bg-gray-200")
    e.currentTarget.classList.remove("hover:bg-gray-100")

    if (this._fabricCanvas && this._fabricCanvas.freeDrawingBrush) {
      this._fabricCanvas.freeDrawingBrush.width = this._currentWidth
    }
  }

  // ── Shape drawing ─────────────────────────────────────────────────

  _onMouseDown(opt) {
    if (this._currentTool === "pen" || this._currentTool === "select" || !this._fabricCanvas) return

    const pointer = this._fabricCanvas.getScenePoint(opt.e)
    this._isDrawingShape = true
    this._shapeStartX = pointer.x
    this._shapeStartY = pointer.y

    const { Circle, Rect, Line, Textbox } = this._fabric

    if (this._currentTool === "circle") {
      this._activeShape = new Circle({
        left: pointer.x,
        top: pointer.y,
        radius: 0,
        fill: "transparent",
        stroke: this._currentColor,
        strokeWidth: this._currentWidth,
        selectable: true
      })
      this._fabricCanvas.add(this._activeShape)
    } else if (this._currentTool === "rect") {
      this._activeShape = new Rect({
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
        fill: "transparent",
        stroke: this._currentColor,
        strokeWidth: this._currentWidth,
        selectable: true
      })
      this._fabricCanvas.add(this._activeShape)
    } else if (this._currentTool === "arrow") {
      this._activeShape = new Line([pointer.x, pointer.y, pointer.x, pointer.y], {
        stroke: this._currentColor,
        strokeWidth: this._currentWidth,
        selectable: true
      })
      this._fabricCanvas.add(this._activeShape)
    } else if (this._currentTool === "text") {
      const textbox = new Textbox("Text", {
        left: pointer.x,
        top: pointer.y,
        fontSize: 20,
        fill: this._currentColor,
        fontFamily: "sans-serif",
        selectable: true,
        editable: true
      })
      this._fabricCanvas.add(textbox)
      this._fabricCanvas.setActiveObject(textbox)
      textbox.enterEditing()
      this._isDrawingShape = false
    }
  }

  _onMouseMove(opt) {
    if (!this._isDrawingShape || !this._activeShape) return

    const pointer = this._fabricCanvas.getScenePoint(opt.e)

    if (this._currentTool === "circle") {
      const radius = Math.sqrt(
        Math.pow(pointer.x - this._shapeStartX, 2) + Math.pow(pointer.y - this._shapeStartY, 2)
      ) / 2
      this._activeShape.set({
        radius: Math.abs(radius),
        left: Math.min(this._shapeStartX, pointer.x),
        top: Math.min(this._shapeStartY, pointer.y)
      })
    } else if (this._currentTool === "rect") {
      const w = pointer.x - this._shapeStartX
      const h = pointer.y - this._shapeStartY
      this._activeShape.set({
        width: Math.abs(w),
        height: Math.abs(h),
        left: w > 0 ? this._shapeStartX : pointer.x,
        top: h > 0 ? this._shapeStartY : pointer.y
      })
    } else if (this._currentTool === "arrow") {
      this._activeShape.set({ x2: pointer.x, y2: pointer.y })
    }

    this._fabricCanvas.renderAll()
  }

  _onMouseUp() {
    if (!this._isDrawingShape) return
    this._isDrawingShape = false

    // Add arrowhead for arrow tool
    if (this._currentTool === "arrow" && this._activeShape && this._fabric) {
      this._addArrowhead(this._activeShape)
    }

    this._activeShape = null
  }

  _addArrowhead(line) {
    const { Triangle, Group } = this._fabric
    const x1 = line.x1, y1 = line.y1, x2 = line.x2, y2 = line.y2
    const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI)

    const head = new Triangle({
      left: x2,
      top: y2,
      originX: "center",
      originY: "center",
      angle: angle + 90,
      width: this._currentWidth * 4,
      height: this._currentWidth * 4,
      fill: this._currentColor,
      selectable: false
    })

    this._fabricCanvas.add(head)
  }

  // ── History ───────────────────────────────────────────────────────

  _saveToHistory() {
    if (!this._fabricCanvas) return
    this._historyIndex++
    this._history = this._history.slice(0, this._historyIndex)
    const json = this._fabricCanvas.toJSON(["selectable", "evented"])
    // Manually inject annotation metadata (Fabric v7 toJSON doesn't serialize custom props)
    const fabricObjs = this._fabricCanvas.getObjects()
    const objs = json.objects || []
    for (let i = 0; i < objs.length && i < fabricObjs.length; i++) {
      if (fabricObjs[i]._annotatedBy) {
        objs[i]._annotatedBy = fabricObjs[i]._annotatedBy
        objs[i]._annotatedAt = fabricObjs[i]._annotatedAt
      }
    }
    this._history.push(json)
  }

  async undo() {
    if (this._historyIndex <= 0 || !this._fabricCanvas) return
    this._historyIndex--
    await this._restoreFromHistory()
  }

  async redo() {
    if (this._historyIndex >= this._history.length - 1 || !this._fabricCanvas) return
    this._historyIndex++
    await this._restoreFromHistory()
  }

  async _restoreFromHistory() {
    const json = this._history[this._historyIndex]
    if (!json) return
    const rawObjs = json.objects || []
    await this._fabricCanvas.loadFromJSON(json)
    // Restore custom metadata after loadFromJSON
    const loaded = this._fabricCanvas.getObjects()
    for (let i = 0; i < loaded.length && i < rawObjs.length; i++) {
      if (rawObjs[i]._annotatedBy) {
        loaded[i]._annotatedBy = rawObjs[i]._annotatedBy
        loaded[i]._annotatedAt = rawObjs[i]._annotatedAt
      }
    }
    this._fabricCanvas.backgroundImage = this._backgroundImage
    this._fabricCanvas.renderAll()
  }

  deleteSelected() {
    if (!this._fabricCanvas) return
    const active = this._fabricCanvas.getActiveObjects()
    if (active.length) {
      active.forEach(obj => this._fabricCanvas.remove(obj))
      this._fabricCanvas.discardActiveObject()
      this._fabricCanvas.renderAll()
      this._saveToHistory()
    } else {
      // Nothing selected — clear all
      this._fabricCanvas.clear()
      this._fabricCanvas.backgroundImage = this._backgroundImage
      this._fabricCanvas.renderAll()
      this._saveToHistory()
    }
  }

  // ── Save ──────────────────────────────────────────────────────────

  async save() {
    if (!this._fabricCanvas || !this._photoId) return

    const fabricObjs = this._fabricCanvas.getObjects()
    const fullJson = this._fabricCanvas.toJSON(["selectable", "evented"])

    // Fabric.js v7 toJSON doesn't serialize custom properties reliably —
    // manually inject annotation metadata into the serialized objects
    const serializedObjects = fullJson.objects || []
    for (let i = 0; i < serializedObjects.length && i < fabricObjs.length; i++) {
      if (fabricObjs[i]._annotatedBy) {
        serializedObjects[i]._annotatedBy = fabricObjs[i]._annotatedBy
        serializedObjects[i]._annotatedAt = fabricObjs[i]._annotatedAt
      }
    }

    const annotations = {
      version: 1,
      objects: serializedObjects,
      width: this._originalWidth,
      height: this._originalHeight
    }

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const url = this._saveUrl || `${this.baseUrlValue}/${this._photoId}/save_annotation`
      const res = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ annotations, attachment_id: this._photoId })
      })
      const data = await res.json()

      // Update the photos controller
      const photosCtrl = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller~="photos"]'),
        "photos"
      )
      if (photosCtrl) {
        photosCtrl.updatePhotoTile(this._photoId, { has_annotations: true, comment_count: data.comment_count })
      }

      // Update lightbox photo data and re-render overlay
      const lightboxCtrl = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller~="photo-lightbox"]'),
        "photo-lightbox"
      )
      if (lightboxCtrl && lightboxCtrl._photos) {
        const photo = lightboxCtrl._photos.find(p => p.id === this._photoId)
        if (photo) {
          photo.has_annotations = true
          photo.annotations = annotations
          photo.comment_count = data.comment_count
        }
      }

      const savedPhotoId = this._photoId
      const savedAnnotations = annotations

      // Notify caller of saved annotations before closing
      if (this._onSaveCallback) this._onSaveCallback(savedPhotoId, savedAnnotations)

      this.close()

      // Re-render the lightbox annotation overlay and reload comments
      if (lightboxCtrl && lightboxCtrl._index >= 0) {
        lightboxCtrl._renderAnnotationOverlay(lightboxCtrl._photos[lightboxCtrl._index])
        lightboxCtrl._loadComments(savedPhotoId)
      }
    } catch (e) {
      console.error("Failed to save annotations:", e)
      alert("Failed to save annotations")
    }
  }
}
